import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Locale, PersonaEvent } from '@chessmate/persona';
import { pickLine } from '@chessmate/persona';
import { effectiveAudio, useAudioSettingsStore } from './audio-settings-store.js';

/**
 * The persona preference (docs/proposals/personas-slices-execution.md, P3): which character plays
 * the opponent in chess and checkers. The anti-repeat memory is per session, not persisted — a
 * fresh app may repeat an old line, which is fine after days away.
 *
 * How chatty they are lives in the audio settings store (spec: ONE audio settings store), and the
 * level that applies here is the EFFECTIVE one — a grown-up's "chatter off" lock silences the
 * personas without touching this store.
 */

const DEFAULT_OPPONENT = 'marisol';

interface PersonaStore {
  /** The opponent character for the single-robot games (chess, checkers). */
  readonly personaId: string;
  /** Lines this persona said recently (session memory for the anti-repeat window). */
  readonly recent: readonly string[];
  /** Count of lines spoken this game (the limiter resets on newGame). */
  readonly spoken: number;
  setPersona(personaId: string): void;
  /** Reset the per-game limiter counter (call on game start). */
  resetSpoken(): void;
  /**
   * Pick a line for the event, or null when the limiter says quiet. The chosen line is remembered
   * (anti-repeat + spoken counter) — call at most once per event.
   */
  lineFor(event: PersonaEvent, locale: Locale): string | null;
}

export const usePersonaStore = create<PersonaStore>()(
  persist(
    (set, get) => ({
      personaId: DEFAULT_OPPONENT,
      recent: [],
      spoken: 0,
      setPersona(personaId) {
        set({ personaId, recent: [], spoken: 0 });
      },
      resetSpoken() {
        set({ spoken: 0 });
      },
      lineFor(event: PersonaEvent, locale: Locale) {
        const { personaId, recent, spoken } = get();
        const chatter = effectiveAudio(useAudioSettingsStore.getState()).chatter;
        const line = pickLine(
          { personaId, event, locale, spoken, chatter, recent },
        );
        if (line === null) return null;
        set({ recent: [line, ...recent].slice(0, 6), spoken: spoken + 1 });
        return line;
      },
    }),
    {
      name: 's4k:persona',
      partialize: (s) => ({ personaId: s.personaId }),
    },
  ),
);
